import { Component, computed, input } from '@angular/core';
import {
  RdxTooltipModule,
} from '@radix-ng/primitives/tooltip';

@Component({
  selector: 'ds-tooltip',
  imports: [
    RdxTooltipModule,
  ],
  template: `<ng-container
      rdxTooltipRoot
      [defaultOpen]="$defaultOpen()"
      [openDelay]="$openDelay()"
      [closeDelay]="$closeDelay()"
    >
    <div class="ds-tooltip-trigger" rdxTooltipTrigger>
      <ng-content />
    </div>

    <ng-template rdxTooltipContent [align]="$any($alignment())" [side]="$any($position())" [sideOffset]="$separation()">
      <!-- allow closing the tooltip via keystroke -->
      <button class="ds-tooltip-close-button" rdxTooltipClose aria-label="Close"></button>
      <div
      [class]="$classNames()"
      rdxTooltipContentAttributes
      [innerHTML]="$text()"
      [title]="$text()"
      aria-hidden="true"
      [attr.aria-label]="$text()"
      aria-live="polite"
      aria-relevant="text"
      role="tooltip"
      ></div>
    </ng-template>
</ng-container>
`,
  styles: `
    :host {display: contents;}
  `
})
export class TooltipComponent {
  /**
   * Tooltip text to be displayed when hovering over the element.
   * Can be a string or an HTML element.
   * @type {string}
   */
  $text = input.required<string | HTMLElement>({ alias: 'text' });
  /**
   * Tooltip position relative to the element.
   * @type {'top' | 'right' | 'bottom' | 'left'}
   */
  $position = input<'top' | 'right' | 'bottom' | 'left'>('right', { alias: 'position' });
  /**
   * Tooltip alignment relative to the element. By default, it is centered.
   * @type {'start' | 'end' | 'center'}
   */
  $alignment = input<'start' | 'end' | 'center'>('center', { alias: 'alignment' });
  /**
   * Distance in pixels between the tooltip and the host element.
   * @type {number}
   */
  $separation = input<number>(8, { alias: 'separation' });
  /**
   * Tooltip delay in milliseconds before it appears.
   * @type {number}
   */
  $openDelay = input<number>(100, { alias: 'openDelay' });
  /**
   * Tooltip delay in milliseconds before it disappears.
   * @type {number}
   */
  $closeDelay = input<number>(100, { alias: 'closeDelay' });
  /**
   * If true, the tooltip will not be displayed.
   * @type {boolean}
   */
  $disabled = input<boolean>(false, { alias: 'disabled' });
  /**
   * Variant of the tooltip.
   * By default, it is neutral.
   * @type {'primary' | 'secondary' | 'neutral'}
   */
  $variantColor = input<'primary' | 'secondary' | 'neutral'>('neutral', { alias: 'variantColor' });
  /**
   * If true, the tooltip will be open by default.
   * @type {boolean}
   */
  $defaultOpen = input<boolean>(false, { alias: 'defaultOpen' });
  /**
   * CSS class names for the tooltip element.
   * @type {string}
   */
  $classNames = computed<string>(() => [
    'ds-tooltip',
    `ds-tooltip-${this.$variantColor()}`,
    this.$disabled() ? 'ds-tooltip-disabled' : '',
  ].filter((className) => !!className).join(' '));
}
